import { Card } from "../ui/Card";
import { isHeavyEquipmentCategory } from "./heavyEquipmentCatalogue";
import { isSustainableWaterCategory } from "./sustainableWaterCatalogue";
import { formatAud, formatMetric, formatNumber } from "../../utils/format";
import type { JSX } from "react";

interface SubmissionResultProps {
  category: string;
  labId: string;
  calculatedResults?: ResultData;
  // baseline or savings mode controls the title label
  mode?: "baseline" | "savings";
}

interface FieldValue {
  field: JSX.Element;
  value: JSX.Element;
}

interface ResultData {
  cost?: number;
  emissions?: number;
  energy?: number;
  waste?: number;
  storage?: number;
  [key: string]: number | undefined; // Allow other numeric keys
}

/** Unit suffix per result key. Cost and units format differently, see below. */
const RESULT_UNITS: Record<string, string> = {
  emissions: "kgCO2e",
  emissions_kgco2e: "kgCO2e",
  energy: "kWh",
  energy_mj: "MJ",
  waste: "kg",
  storage: "TB",
  volume_ml: "mL",
  water_l: "L",
};

// Define column configurations for each category
const CATEGORY_COLUMNS: Record<string, Record<string, FieldValue>> = {
  "Computer Hardware": {
    energy: {
      field: (
        <span>
          <strong>Energy</strong> (kWh)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  "Heavy Equipment": {
    energy: {
      field: (
        <span>
          <strong>Energy</strong> (kWh)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  "Eco Office": {
    waste: {
      field: (
        <span>
          <strong>Waste</strong> (kg)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  "Data Storage": {
    energy: {
      field: (
        <span>
          <strong>Energy</strong> (kWh)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    storage: {
      field: (
        <span>
          <strong>Storage</strong> (TB)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  "Cold Storage": {
    energy: {
      field: (
        <span>
          <strong>Energy</strong> (kWh)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  "Lab Waste": {
    waste: {
      field: (
        <span>
          <strong>Waste</strong> (kg)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  Transport: {
    energy_mj: {
      field: (
        <span>
          <strong>Energy</strong> (MJ)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions_kgco2e: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  Procurement: {
    units: {
      field: (
        <span>
          <strong>Units Procured</strong>
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    waste: {
      field: (
        <span>
          <strong>Waste</strong> (kg)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  "Sustainable Water": {
    water_l: {
      field: (
        <span>
          <strong>Water</strong> (L)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    energy: {
      field: (
        <span>
          <strong>Energy</strong> (kWh)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  "Green Chemistry": {
    volume_ml: {
      field: (
        <span>
          <strong>Volume</strong> (mL)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    toxicity_burden: {
      field: (
        <span>
          <strong>Toxicity Burden</strong>
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
};

/**
 * The column set for a category, matching the aliases the rest of the app
 * accepts.
 *
 * Two assessments answer to a second name, because `catalog_metadata()` and the
 * enum disagree: Heavy Equipment is also "Equipment" and Sustainable Water is
 * also "Water". An exact lookup silently renders "No data available" for the
 * other spelling even though the mapper produced the totals.
 */
const columnsFor = (
  category: string,
): Record<string, FieldValue> | undefined => {
  if (isHeavyEquipmentCategory(category)) {
    return CATEGORY_COLUMNS["Heavy Equipment"];
  }
  if (isSustainableWaterCategory(category)) {
    return CATEGORY_COLUMNS["Sustainable Water"];
  }
  return CATEGORY_COLUMNS[category];
};

/**
 * Once a baseline calculator has been submitted, this result component is shown
 * @constructor
 */
export const SubmissionResult = ({
  category,
  calculatedResults,
  mode = "baseline",
}: SubmissionResultProps) => {
  // Get the column configuration for the current category
  const categoryColumns = columnsFor(category);

  if (!categoryColumns) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">
          {mode === "baseline"
            ? "Projected Annual Cost"
            : "Projected Annual Savings"}
        </h2>
        <p className="text-gray-600">
          No data available for category: {category}
        </p>
      </Card>
    );
  }

  const getFormattedValue = (key: string): JSX.Element => {
    const value = calculatedResults ? calculatedResults[key] : undefined;

    if (value === undefined || value === null) {
      return <span className="text-gray-400 font-normal">N/A</span>;
    }

    if (key === "cost") {
      return <span>{formatAud(value)}</span>;
    }

    // A procured-unit count, not a measurement: "1,240.00 units" reads wrong.
    if (key === "units") {
      return <span>{formatNumber(value)}</span>;
    }

    const unit = RESULT_UNITS[key];
    return (
      <span>
        {formatMetric(value)}
        {unit ? ` ${unit}` : ""}
      </span>
    );
  };

  return (
    <Card className="p-6">
      <h2 className="text-xl font-bold mb-4">
        {mode === "baseline"
          ? "Projected Annual Cost"
          : "Projected Annual Savings"}
      </h2>
      <div className="overflow-x-auto">
        <div className="card-content">
          <table className="table-auto">
            <tbody>
              <tr>
                {Object.entries(categoryColumns).map(
                  ([key, fieldValue], index, arr) => (
                    <td
                      key={key}
                      className={`${index === 0 ? "pr-4" : index === arr.length - 1 ? "pl-4" : "px-4"} py-2 font-medium`}
                    >
                      {fieldValue.field}
                    </td>
                  ),
                )}
              </tr>
              <tr>
                {Object.keys(categoryColumns).map((key, index, arr) => (
                  <td
                    key={"value-" + key}
                    className={`${index === 0 ? "pr-4" : index === arr.length - 1 ? "pl-4" : "px-4"} py-2 text-right`}
                  >
                    {getFormattedValue(key)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
};
